import { eachSeries as asyncEachSeries } from 'async';
import React from 'react';
import EventEmitter from 'events';

import util from '../tools/util';
import UserStore from './user_store';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isReady,
  getList,
  useList,
  useUser,
  fetch,
  addUser,
  updateUser,
  addVolume,
  deleteVolumeList,
  removeUserOrg,
};

const g_orgUserListMap: Map<number, any[]> = new Map();

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init(done) {
  done?.();
}
function isReady() {
  return true;
}
function getList(org_id: number) {
  return g_orgUserListMap.get(org_id);
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useList(org_id: number) {
  const _get = React.useCallback(() => {
    return getList(org_id);
  }, [org_id]);
  return React.useSyncExternalStore(_subscribe, _get);
}
export function useUser(org_id: number, user_id: number) {
  const list = useList(org_id);
  return list?.find?.((user) => user.user_id === user_id);
}
function fetch(org_id: number, done?: (err: any) => void) {
  const opts = {
    url: `/api/1/org/${org_id}/user`,
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('UserAdminStore.fetch: err:', err);
    } else if (body) {
      const old = g_orgUserListMap.get(org_id);
      if (!util.deepEqual(old, body.user_list)) {
        g_orgUserListMap.set(org_id, body.user_list);
        _emit('update');
      }
    }
    done?.(err);
  });
}
function addUser(params, done) {
  const { org_id, ...other } = params;
  const opts = {
    url: `/api/1/org/${org_id}/user`,
    body: other,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('UserAdminStore.addUser: err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done(null, body.user_id));
    }
  });
}
function updateUser(params, done) {
  const { org_id, user_id, is_admin, cap_add_device } = params;
  const opts = {
    url: `/api/1/org/${org_id}/user/${user_id}`,
    body: {
      is_admin,
      cap_add_device,
    },
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('UserAdminStore.updateUser: err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
function addVolume(params, done) {
  const { org_id, user_id, volume_id } = params;
  const opts = {
    url: `/api/1/org/${org_id}/user/${user_id}/volume/${volume_id}`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('UserAdminStore.addVolume: err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
function deleteVolumeList(params, done) {
  const { org_id, user_id, volume_list } = params;
  asyncEachSeries(
    volume_list,
    (volume: any, done) => {
      const { volume_id } = volume;
      const opts = {
        url: `/api/1/org/${org_id}/user/${user_id}/volume/${volume_id}`,
      };
      UserStore.del(opts, (err, body) => {
        if (err) {
          util.errorLog('UserAdminStore.deleteVolumeList: err:', err, body);
        }
        done(err);
      });
    },
    (err) => fetch(org_id, () => done(err))
  );
}
function removeUserOrg(params, done) {
  const { org_id, user_id } = params;
  const opts = {
    url: `/api/1/org/${org_id}/user/${user_id}`,
  };
  UserStore.del(opts, (err, body) => {
    if (err) {
      util.errorLog('UserAdminStore.removeUserOrg: err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
